import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase/init';
import { User } from '../../types';
import { COLLECTIONS } from '../../config/constants';
import { logger } from '../../utils/logger';
import { getRestaurantId } from '../../utils/restaurant';
import { customerProfileService } from './customerProfile.service';

const requireCurrentUser = (userId: string) => {
  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.uid !== userId) {
    throw new Error('The signed-in account does not match the requested customer profile.');
  }
};

export const userService = {
  async createUser(userId: string, userData: Omit<User, 'id'>): Promise<void> {
    try {
      requireCurrentUser(userId);
      const restaurantId = getRestaurantId();
      logger.info('Provisioning customer profile', { userId, restaurantId });
      await customerProfileService.ensure({
        name: userData.name,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
      });
      logger.info('Customer profile provisioned successfully');
    } catch (error) {
      logger.error('Error creating user profile:', error);
      throw error;
    }
  },

  async getUser(userId: string): Promise<User | null> {
    try {
      logger.info('Fetching user profile', { userId });
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        logger.warn('No user profile found', { userId });
        return null;
      }

      const userData = userDoc.data();
      logger.info('User profile fetched successfully');

      // Ensure backward compatibility
      const restaurantIds = userData.restaurantIds || (userData.restaurantId ? [userData.restaurantId] : []);

      return {
        id: userDoc.id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        phoneNumber: userData.phoneNumber,
        phoneVerified: userData.phoneVerified || false,
        phoneVerifiedAt: userData.phoneVerifiedAt,
        restaurantId: userData.restaurantId,
        restaurantIds,
        employeeCode: userData.employeeCode,
        internalEmail: userData.internalEmail,
        authMode: userData.authMode,
        activationStatus: userData.activationStatus,
        activatedDeviceCount: userData.activatedDeviceCount,
        lastActivatedAt: userData.lastActivatedAt,
        hasWorkstationPin: userData.hasWorkstationPin,
        hasApprovalPin: userData.hasApprovalPin,
        assistantPermissionDenials: userData.assistantPermissionDenials,
        assistantAutonomyTrusted: userData.assistantAutonomyTrusted,
        assistantPermissionsUpdatedAt: userData.assistantPermissionsUpdatedAt,
        assistantPermissionsUpdatedBy: userData.assistantPermissionsUpdatedBy,
      };
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      throw error;
    }
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      logger.info('Updating user profile', { userId, updates });
      const userRef = doc(db, COLLECTIONS.USERS, userId);

      await updateDoc(userRef, {
        ...updates,
        updatedAt: new Date().toISOString(),
      });

      logger.info('User profile updated successfully');
    } catch (error) {
      logger.error('Error updating user profile:', error);
      throw error;
    }
  },

  async linkRestaurant(userId: string): Promise<void> {
    try {
      requireCurrentUser(userId);
      const restaurantId = getRestaurantId();
      logger.info('Linking customer to restaurant', { userId, restaurantId });
      await customerProfileService.ensure();
      logger.info('Customer linked to restaurant successfully');
    } catch (error) {
      logger.error('Error linking user to restaurant:', error);
      throw error;
    }
  },
};
